import { urls } from 'config/urls';
import { useCallback, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SITE_KEY = urls.TURNSTILE_SITE_KEY;
const ACTION = urls.TURNSTILE_ACTION;
const DISABLED = urls.TURNSTILE_DISABLED;
const TOKEN_WAIT_MS = 20000;

export class TurnstileError extends Error {
  constructor() {
    super('turnstile verification unavailable');
    this.name = 'TurnstileError';
  }
}

type TurnstileApi = {
  render: (el: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_WAIT_MS = 20000;
const SCRIPT_POLL_MS = 50;

let scriptPromise: Promise<void> | null = null;

const loadScript = (): Promise<void> => {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const fail = () => {
      scriptPromise = null;
      reject(new Error('turnstile script failed to load'));
    };

    if (!document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)) {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onerror = fail;
      document.head.appendChild(script);
    }

    let waited = 0;
    const interval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(interval);
        resolve();
        return;
      }
      waited += SCRIPT_POLL_MS;
      if (waited >= SCRIPT_WAIT_MS) {
        clearInterval(interval);
        fail();
      }
    }, SCRIPT_POLL_MS);
  });

  return scriptPromise;
};

type Waiter = {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
};

export const useTurnstile = () => {
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef('');
  const waitersRef = useRef<Waiter[]>([]);
  const scriptFailedRef = useRef(false);
  const nodeRef = useRef<HTMLDivElement | null>(null);

  const regenerate = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  const handleToken = useCallback(
    (token: string) => {
      const waiter = waitersRef.current.shift();
      if (waiter) {
        waiter.resolve(token);
        regenerate();
        return;
      }
      tokenRef.current = token;
    },
    [regenerate]
  );

  const renderWidget = useCallback(
    (node: HTMLDivElement) => {
      if (!window.turnstile || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(node, {
        sitekey: SITE_KEY,
        action: ACTION,
        appearance: 'interaction-only',
        callback: handleToken,
        'error-callback': () => {
          tokenRef.current = '';
        },
        'expired-callback': () => {
          tokenRef.current = '';
        },
      });
    },
    [handleToken]
  );

  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (DISABLED) return;
      if (node) {
        nodeRef.current = node;
        loadScript()
          .then(() => {
            if (nodeRef.current === node) renderWidget(node);
          })
          .catch(() => {
            scriptFailedRef.current = true;
            waitersRef.current
              .splice(0)
              .forEach((waiter) => waiter.reject(new TurnstileError()));
          });
        return;
      }
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
      tokenRef.current = '';
      nodeRef.current = null;
      waitersRef.current.splice(0).forEach((waiter) => waiter.resolve(''));
    },
    [renderWidget]
  );

  const getToken = useCallback((): Promise<string> => {
    if (DISABLED) return Promise.resolve('');
    if (scriptFailedRef.current) {
      return Promise.reject(new TurnstileError());
    }
    if (tokenRef.current) {
      const token = tokenRef.current;
      tokenRef.current = '';
      regenerate();
      return Promise.resolve(token);
    }
    return new Promise<string>((resolve, reject) => {
      const waiter: Waiter = { resolve, reject };
      const timeout = setTimeout(() => {
        const index = waitersRef.current.indexOf(waiter);
        if (index !== -1) waitersRef.current.splice(index, 1);
        reject(new TurnstileError());
      }, TOKEN_WAIT_MS);
      waiter.resolve = (token) => {
        clearTimeout(timeout);
        resolve(token);
      };
      waiter.reject = (error) => {
        clearTimeout(timeout);
        reject(error);
      };
      waitersRef.current.push(waiter);
    });
  }, [regenerate]);

  return { containerRef, getToken, reset: regenerate };
};
